import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, XCircle, Calendar, Clock, User, AlertCircle } from 'lucide-react';
import { publicAPI } from '../services/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const ConfirmAppointment = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error, invalid
  const [appointment, setAppointment] = useState(null);
  const [action, setAction] = useState('');

  useEffect(() => {
    processConfirmation();
  }, []);

  const processConfirmation = async () => {
    // Check for short URL format (/c?i=xxx&a=1)
    const shortId = searchParams.get('i');
    const actionCode = searchParams.get('a');
    
    // Also support long format
    const longId = searchParams.get('id');
    const longAction = searchParams.get('action');
    
    console.log('URL params:', { shortId, actionCode, longId, longAction });
    
    if (!shortId && !longId) {
      console.log('No ID found in params');
      setStatus('invalid');
      return;
    }

    try {
      const params = {};
      
      if (shortId && actionCode !== null) {
        // Short URL format
        params.i = shortId;
        params.a = actionCode;
        setAction(actionCode === '1' ? 'confirm' : 'cancel');
        console.log('Using short format params:', params);
      } else if (longId && longAction) {
        // Long URL format
        params.id = longId;
        params.action = longAction;
        setAction(longAction);
        console.log('Using long format params:', params);
      }

      console.log('Calling API with params:', params);
      const response = await publicAPI.confirmAppointment(params);
      console.log('API response:', response.data);
      
      if (response.data.success) {
        setAppointment(response.data.appointment);
        setAction(response.data.action);
        setStatus('success');
      } else {
        console.log('API returned success=false');
        setStatus('error');
      }
    } catch (error) {
      console.error('Error confirming appointment:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      if (error.response?.status === 404) {
        console.log('404 error - appointment not found');
        setStatus('error');
      } else {
        console.log('Other error - marking as invalid');
        setStatus('invalid');
      }
    }
  };

  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-600">Procesando...</div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="bg-red-500 text-white rounded-t-lg">
            <CardTitle className="flex items-center">
              <AlertCircle className="w-6 h-6 mr-2" />
              Link Inválido
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-gray-700 mb-4">
              El link que has utilizado no es válido o ha expirado.
            </p>
            <p className="text-sm text-gray-600">
              Por favor, contacta con el establecimiento para más información.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="bg-red-500 text-white rounded-t-lg">
            <CardTitle className="flex items-center">
              <XCircle className="w-6 h-6 mr-2" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-gray-700 mb-4">
              No se pudo encontrar la cita solicitada.
            </p>
            <p className="text-sm text-gray-600">
              Por favor, verifica el link o contacta con el establecimiento.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  const isConfirm = action === 'confirm';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-2xl">
        <CardHeader className={`${isConfirm ? 'bg-green-500' : 'bg-orange-500'} text-white rounded-t-lg`}>
          <CardTitle className="flex items-center text-2xl">
            {isConfirm ? (
              <><CheckCircle className="w-8 h-8 mr-3" /> ¡Cita Confirmada!</>
            ) : (
              <><XCircle className="w-8 h-8 mr-3" /> Cita Cancelada</>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          <div className="text-center">
            <div className={`w-20 h-20 mx-auto mb-4 rounded-full ${isConfirm ? 'bg-green-100' : 'bg-orange-100'} flex items-center justify-center`}>
              {isConfirm ? (
                <CheckCircle className="w-12 h-12 text-green-600" />
              ) : (
                <XCircle className="w-12 h-12 text-orange-600" />
              )}
            </div>
            <h2 className="text-xl font-semibold mb-2">
              {isConfirm ? '¡Gracias por confirmar!' : 'Cita cancelada'}
            </h2>
            <p className="text-gray-600">
              {isConfirm 
                ? 'Tu cita ha sido confirmada exitosamente. Te esperamos.'
                : 'Tu cita ha sido cancelada. Puedes agendar una nueva cuando lo necesites.'}
            </p>
          </div>

          {appointment && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-gray-900">Detalles de la cita:</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-2 text-gray-600" />
                  <span className="text-gray-700">{appointment.patientName}</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-gray-600" />
                  <span className="text-gray-700">
                    {format(new Date(appointment.date), 'PPP', { locale: es })}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-gray-600" />
                  <span className="text-gray-700">{formatTimeTo12Hour(appointment.time)}</span>
                </div>
                {appointment.serviceType && (
                  <div className="pt-2 border-t">
                    <span className="text-gray-600">Servicio: </span>
                    <span className="font-medium text-gray-900">{appointment.serviceType}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="pt-4">
            <p className="text-center text-sm text-gray-600">
              Si necesitas hacer cambios, por favor contacta con el establecimiento.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfirmAppointment;